import React, { useState } from 'react';
import { compose } from 'redux';
import { withRouter } from 'react-router-dom';
import { array, bool, func, node, number, object, oneOfType, shape, string } from 'prop-types';
import loadable from '@loadable/component';
import classNames from 'classnames';
import omit from 'lodash/omit';

import { intlShape, injectIntl, FormattedMessage } from '../../util/reactIntl';
import {
  propTypes,
  LISTING_STATE_CLOSED,
  LINE_ITEM_NIGHT,
  LINE_ITEM_DAY,
  LINE_ITEM_ITEM,
  LINE_ITEM_HOUR,
} from '../../util/types';
import { formatMoney, priceData, subUnitsToMoney } from '../../util/currency';
import { parse, stringify } from '../../util/urlHelpers';
import {
  getSupportedProcessesInfo,
  isBookingProcess,
  resolveLatestProcessName,
} from '../../transactions/transaction';

import {
  ModalInMobile,
  PrimaryButton,
  AvatarSmall,
  H1,
  H2,
  UserCard,
  ListingSellerCard,
  H4,
  SecondaryButton,
  Modal,
} from '../../components';
import { LISTING_INTENT_TYPE, LISTING_STATUS } from '../../constants/listing';
import { useSelector } from 'react-redux';
import { CURRENCIES_BY_LOCALE, getCurrentLocale } from '../../util/locale';
import { isMarketplaceRespare, isMarketplaceSecondLife } from '../../util/marketplace';
import { useConfiguration } from '../../context/configurationContext';
import ContractLeaseActions from '../ContractLeaseActions/ContractLeaseActions';
import {
  getNumberOfBuyingRequests,
  hasAnyBuyingRequest,
  isEndingSoon,
  isExpired,
  isListingOwner,
} from '../../util/listing';
import api from '../../api';
import { isUserVerifiedAdmin } from '../../util/user';

import css from './OrderPanel.module.css';

const BookingTimeForm = loadable(() =>
  import(/* webpackChunkName: "BookingTimeForm" */ './BookingTimeForm/BookingTimeForm')
);
const BookingDatesForm = loadable(() =>
  import(/* webpackChunkName: "BookingDatesForm" */ './BookingDatesForm/BookingDatesForm')
);
const ProductOrderForm = loadable(() =>
  import(/* webpackChunkName: "ProductOrderForm" */ './ProductOrderForm/ProductOrderForm')
);

// This defines when ModalInMobile shows content as Modal
const MODAL_BREAKPOINT = 1023;
const TODAY = new Date();

const openOrderModal = (isOwnListing, isClosed, history, location) => {
  if (isOwnListing || isClosed) {
    window.scrollTo(0, 0);
  } else {
    const { pathname, search, state } = location;
    const searchString = `?${stringify({ ...parse(search), orderOpen: true })}`;
    history.push(`${pathname}${searchString}`, state);
  }
};

const closeOrderModal = (history, location) => {
  const { pathname, search, state } = location;
  const searchParams = omit(parse(search), 'orderOpen');
  const searchString = `?${stringify(searchParams)}`;
  history.push(`${pathname}${searchString}`, state);
};

const dateFormattingOptions = { month: 'short', day: 'numeric', weekday: 'short' };

const OrderPanel = props => {
  const {
    rootClassName,
    className,
    titleClassName,
    modalClassName,
    listing,
    lineItemUnitType: lineItemUnitTypeMaybe,
    isOwnListing,
    onSubmit,
    title,
    titleDesktop,
    author,
    onManageDisableScrolling,
    onFetchTimeSlots,
    monthlyTimeSlots,
    history,
    location,
    intl,
    onFetchTransactionLineItems,
    onContactUser,
    lineItems,
    marketplaceCurrency,
    dayCountAvailableForBooking,
    marketplaceName,
    fetchLineItemsInProgress,
    fetchLineItemsError,
    onOfferListing,
    handleUpdatedListing,
    QRCode,
    contactOwner,
    isAuthenticated,
  } = props;

  const config = useConfiguration();

  const { currentUser } = useSelector(state => state.user);

  const is2ndLifeMarketplace = isMarketplaceSecondLife(config);

  const transactionProcessAlias = listing?.attributes?.publicData?.transactionProcessAlias || '';
  const processName = resolveLatestProcessName(transactionProcessAlias.split('/')[0]);
  const unitType = listing?.attributes?.publicData?.unitType;
  const lineItemUnitType = lineItemUnitTypeMaybe || `line-item/${unitType}`;

  const { publicData, metadata } = listing?.attributes || {};
  const { intentType, pickupEnabled, shippingEnabled, husqvarnaCertified, dimrGenericNameShort } =
    publicData || {};

  const { status, buyingRequests } = metadata || {};

  const price = listing?.attributes?.price;

  const [confirmModal, setConfirmModal] = useState({ open: false, request: null });
  const [declineModal, setDeclineModal] = useState({ open: false, request: null });
  const [busyFor, setBusyFor] = useState(null);
  const [requestsOpen, setRequestsOpen] = useState(false);

  const showPriceMissing = isMarketplaceRespare(config) && !price;
  const PriceMissing = () => {
    return (
      <p className={css.error}>
        <FormattedMessage id="OrderPanel.listingPriceMissing" />
      </p>
    );
  };
  const locale = getCurrentLocale();

  const showInvalidCurrency =
    isMarketplaceRespare(config) &&
    CURRENCIES_BY_LOCALE[locale].every(({ code }) => code !== price?.currency) &&
    price?.currency !== marketplaceCurrency;

  const InvalidCurrency = () => {
    return (
      <p className={css.error}>
        <FormattedMessage id="OrderPanel.listingCurrencyInvalid" />
      </p>
    );
  };

  const timeZone = listing?.attributes?.availabilityPlan?.timezone;
  const isClosed = listing?.attributes?.state === LISTING_STATE_CLOSED;

  const isBooking = isBookingProcess(processName);
  const shouldHaveBookingTime = isBooking && [LINE_ITEM_HOUR].includes(lineItemUnitType);
  const showBookingTimeForm = shouldHaveBookingTime && !isClosed && timeZone;

  const shouldHaveBookingDates =
    isBooking && [LINE_ITEM_DAY, LINE_ITEM_NIGHT].includes(lineItemUnitType);
  const showBookingDatesForm = shouldHaveBookingDates && !isClosed && timeZone;

  // The listing resource has a relationship: `currentStock`,
  // which you should include when making API calls.
  const currentStock = listing.currentStock?.attributes?.quantity;
  const isOutOfStock = lineItemUnitType === LINE_ITEM_ITEM && currentStock === 0;

  // Show form only when stock is fully loaded. This avoids "Out of stock" UI by
  // default before all data has been downloaded.
  const shouldHaveProductOrder = !isBooking && [LINE_ITEM_ITEM].includes(lineItemUnitType);
  const showProductOrderForm = shouldHaveProductOrder && typeof currentStock === 'number';

  const supportedProcessesInfo = getSupportedProcessesInfo();
  const isKnownProcess = supportedProcessesInfo.map(info => info.name).includes(processName);

  const isRequesting = intentType === LISTING_INTENT_TYPE.REQUESTING;
  const showClosedListingHelpText = listing.id && isClosed;
  const { formattedPrice, priceTitle } = priceData(price, marketplaceCurrency, intl);

  const isOrderOpen = !!parse(location.search).orderOpen;

  const isListingExpired = isExpired(listing);
  const isListingEndingSoon = isEndingSoon(listing);

  const subTitleText = showClosedListingHelpText
    ? intl.formatMessage({ id: 'OrderPanel.subTitleClosedListing' })
    : null;

  const classes = classNames(rootClassName || css.root, className);
  const titleClasses = classNames(titleClassName || css.orderTitle);

  const openOfferListingModal = () => {
    if (isOwnListing || isClosed) {
      window.scrollTo(0, 0);

      return;
    }

    onOfferListing();
  };

  const hasBuyingRequests = hasAnyBuyingRequest(listing);
  const noOfBuyingRequests = hasBuyingRequests ? getNumberOfBuyingRequests(listing) : undefined;

  const acceptRequest = async req => {
    if (!req) return;
    const listingId = listing?.id?.uuid || listing?.id;
    const { companyId, companyName } = req;

    setBusyFor(companyId);
    try {
      const res = await api.listings.acceptBuyingRequest(listingId, companyId);

      handleUpdatedListing?.(res?.data);

      alert(intl.formatMessage({ id: 'OrderPanel.acceptSuccess' }, { company: companyName }));
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
      alert(
        intl.formatMessage({
          id: 'OrderPanel.acceptError',
        })
      );
    } finally {
      setBusyFor(null);
      setConfirmModal({ open: false, request: null });
    }
  };

  // TO DO: this should be updated once we solve the 500 error issue
  const declineRequest = async req => {
    if (!req) return;
    const listingId = listing?.id?.uuid || listing?.id;
    const companyId = req.companyId;

    setBusyFor(companyId);
    try {
      const res = await api.listings.declineBuyingRequest(listingId, companyId);
      const updated = res?.data?.listing || res?.data || null;

      if (updated) {
        handleUpdatedListing?.(updated);
      } else {
        // fallback: remove that request from the list
        const current = listing?.attributes?.metadata?.buyingRequests || [];
        const fallback = {
          ...listing,
          attributes: {
            ...listing.attributes,
            metadata: {
              ...listing?.attributes?.metadata,
              buyingRequests: current.filter(r => r.companyId !== companyId),
            },
          },
        };
        handleUpdatedListing?.(fallback);
      }

      alert(intl.formatMessage({ id: 'OrderPanel.declineSuccess' }, { company: req.companyName }));
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
      alert(
        intl.formatMessage({
          id: 'OrderPanel.declineError',
        })
      );
    } finally {
      setBusyFor(null);
      setDeclineModal({ open: false, request: null });
    }
  };

  return (
    <div className={classes}>
      <ModalInMobile
        className={modalClassName}
        containerClassName={css.modalContainer}
        id="OrderFormInModal"
        isModalOpenOnMobile={isOrderOpen}
        onClose={() => closeOrderModal(history, location)}
        showAsModalMaxWidth={MODAL_BREAKPOINT}
        onManageDisableScrolling={onManageDisableScrolling}
        usePortal
      >
        <div className={css.modalHeading}>
          <H1 className={css.heading}>{title}</H1>
        </div>

        <div className={css.orderHeading}>
          {dimrGenericNameShort && (
            <H4 className={css.genericNameShort}>{dimrGenericNameShort.toLowerCase()}</H4>
          )}
          {titleDesktop ? titleDesktop : <H2 className={titleClasses}>{title}</H2>}
          {subTitleText ? <div className={css.orderHelp}>{subTitleText}</div> : null}
        </div>

        {price || (is2ndLifeMarketplace && publicData?.residualValue) || isRequesting ? (
          <div className={css.priceContainer}>
            {isRequesting ? (
              <p className={css.price}>
                <FormattedMessage id="General.requesting" />
              </p>
            ) : (
              <>
                {is2ndLifeMarketplace &&
                  isAuthenticated &&
                  formatMoney(
                    intl,
                    subUnitsToMoney(
                      publicData?.residualValue,
                      publicData?.locale === 'no' ? 'NOK' : 'SEK'
                    )
                  )}

                {is2ndLifeMarketplace && !isAuthenticated && formatMoney(intl, price)}

                {!is2ndLifeMarketplace && price ? (
                  <p className={css.price}>{formatMoney(intl, price)}</p>
                ) : null}

                <div className={css.perUnit}>
                  <FormattedMessage id="OrderPanel.perUnit" values={{ unitType }} />
                </div>
              </>
            )}
          </div>
        ) : null}

        {isMarketplaceSecondLife(config) ? (
          <ListingSellerCard className={css.author} listing={listing} config={config} />
        ) : (
          <UserCard
            className={css.author}
            user={author}
            currentUser={currentUser}
            onContactUser={onContactUser}
            customAvatar={<AvatarSmall user={author} className={css.providerAvatar} />}
            hideBio
            hideContact={isRequesting}
          />
        )}

        {is2ndLifeMarketplace &&
          (isListingOwner(listing, currentUser) || isUserVerifiedAdmin(currentUser)) &&
          Array.isArray(buyingRequests) &&
          buyingRequests.length > 0 && (
            <div className={css.buyingRequestsWrapper}>
              <button
                type="button"
                className={css.buyingRequestsHeader}
                aria-expanded={requestsOpen}
                onClick={() => setRequestsOpen(o => !o)}
              >
                <span>
                  <FormattedMessage id="OrderPanel.buyingRequestsHeader" />
                </span>
                <span className={css.buyingRequestsHeaderRight}>
                  <span className={css.badge}>{buyingRequests.length}</span>
                  <span className={css.chevron} aria-hidden>
                    {requestsOpen ? '▾' : '▸'}
                  </span>
                </span>
              </button>

              <div
                className={`${css.buyingRequestsPanel} ${
                  requestsOpen ? css.expanded : css.collapsed
                }`}
              >
                {buyingRequests.map(({ companyId, companyName, requestedAt }, idx) => {
                  const key = companyId ?? idx;
                  const dateLabel = requestedAt ? new Date(requestedAt).toLocaleDateString() : '';

                  return (
                    <div key={key} className={css.buyingRequestRow}>
                      <div className={css.buyingRequestText}>
                        <b>{companyName}</b> has requested to buy on {dateLabel && `${dateLabel}`}
                      </div>

                      <div className={css.buyingRequestActions}>
                        <PrimaryButton
                          type="button"
                          onClick={() =>
                            setConfirmModal({
                              open: true,
                              request: { companyId, companyName, requestedAt },
                            })
                          }
                          disabled={busyFor === companyId}
                        >
                          <FormattedMessage id="General.confirm" />
                        </PrimaryButton>

                        <SecondaryButton
                          type="button"
                          className={css.declineBtn}
                          onClick={() =>
                            setDeclineModal({
                              open: true,
                              request: { companyId, companyName, requestedAt },
                            })
                          }
                          disabled={busyFor === companyId}
                        >
                          <FormattedMessage id="General.decline" />
                        </SecondaryButton>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

        {is2ndLifeMarketplace && (
          <ContractLeaseActions
            listing={listing}
            className={css.leaseActions}
            handleUpdatedListing={handleUpdatedListing}
          />
        )}

        {is2ndLifeMarketplace && QRCode}

        {is2ndLifeMarketplace && contactOwner}

        {isRequesting ? (
          <PrimaryButton type="button" onClick={openOfferListingModal}>
            <FormattedMessage id="OrderPanel.ctaButtonMessagePlaceAnOffer" />
          </PrimaryButton>
        ) : showPriceMissing ? (
          <PriceMissing />
        ) : showInvalidCurrency ? (
          <InvalidCurrency />
        ) : showBookingTimeForm ? (
          <BookingTimeForm
            className={css.bookingForm}
            formId="OrderPanelBookingTimeForm"
            lineItemUnitType={lineItemUnitType}
            onSubmit={onSubmit}
            price={price}
            marketplaceCurrency={marketplaceCurrency}
            dayCountAvailableForBooking={dayCountAvailableForBooking}
            listingId={listing.id}
            isOwnListing={isOwnListing}
            monthlyTimeSlots={monthlyTimeSlots}
            onFetchTimeSlots={onFetchTimeSlots}
            startDatePlaceholder={intl.formatDate(TODAY, dateFormattingOptions)}
            endDatePlaceholder={intl.formatDate(TODAY, dateFormattingOptions)}
            timeZone={timeZone}
            marketplaceName={marketplaceName}
            onFetchTransactionLineItems={onFetchTransactionLineItems}
            lineItems={lineItems}
            fetchLineItemsInProgress={fetchLineItemsInProgress}
            fetchLineItemsError={fetchLineItemsError}
          />
        ) : showBookingDatesForm ? (
          <BookingDatesForm
            className={css.bookingForm}
            formId="OrderPanelBookingDatesForm"
            lineItemUnitType={lineItemUnitType}
            onSubmit={onSubmit}
            price={price}
            marketplaceCurrency={marketplaceCurrency}
            dayCountAvailableForBooking={dayCountAvailableForBooking}
            listingId={listing.id}
            isOwnListing={isOwnListing}
            monthlyTimeSlots={monthlyTimeSlots}
            onFetchTimeSlots={onFetchTimeSlots}
            timeZone={timeZone}
            marketplaceName={marketplaceName}
            onFetchTransactionLineItems={onFetchTransactionLineItems}
            lineItems={lineItems}
            fetchLineItemsInProgress={fetchLineItemsInProgress}
            fetchLineItemsError={fetchLineItemsError}
          />
        ) : showProductOrderForm ? (
          <ProductOrderForm
            formId="OrderPanelProductOrderForm"
            onSubmit={onSubmit}
            price={price}
            marketplaceCurrency={marketplaceCurrency}
            currentStock={currentStock}
            pickupEnabled={pickupEnabled}
            shippingEnabled={shippingEnabled}
            listingId={listing.id}
            isOwnListing={isOwnListing}
            marketplaceName={marketplaceName}
            onFetchTransactionLineItems={onFetchTransactionLineItems}
            onContactUser={onContactUser}
            lineItems={lineItems}
            fetchLineItemsInProgress={fetchLineItemsInProgress}
            fetchLineItemsError={fetchLineItemsError}
          />
        ) : !isKnownProcess ? (
          <p className={css.errorSidebar}>
            <FormattedMessage id="OrderPanel.unknownTransactionProcess" />
          </p>
        ) : null}
      </ModalInMobile>

      {/* CONFIRM MODAL */}
      <Modal
        id="ConfirmationModal"
        isOpen={confirmModal.open}
        onClose={() => setConfirmModal({ open: false, request: null })}
        containerClassName={css.confirmModal}
        onManageDisableScrolling={onManageDisableScrolling}
      >
        <H2 className={css.modalTitle}>
          <FormattedMessage id="OrderPanel.acceptTitle" />
        </H2>
        <p className={css.modalText}>
          <FormattedMessage
            id="OrderPanel.acceptDesc"
            values={{ company: <b>{confirmModal.request?.companyName}</b> }}
          />
        </p>
        <div className={css.modalActions}>
          <PrimaryButton
            onClick={() => acceptRequest(confirmModal.request)}
            disabled={busyFor !== null}
          >
            <FormattedMessage id="General.accept" />
          </PrimaryButton>
          <SecondaryButton
            onClick={() => setConfirmModal({ open: false, request: null })}
            disabled={busyFor !== null}
          >
            <FormattedMessage id="General.cancel" />
          </SecondaryButton>
        </div>
      </Modal>

      {/* DECLINE MODAL */}
      <Modal
        id="DeclineModal"
        isOpen={declineModal.open}
        onClose={() => setDeclineModal({ open: false, request: null })}
        containerClassName={css.confirmModal}
        onManageDisableScrolling={onManageDisableScrolling}
      >
        <H2 className={css.modalTitle}>
          <FormattedMessage id="OrderPanel.declineTitle" />
        </H2>
        <p className={css.modalText}>
          <FormattedMessage
            id="OrderPanel.declineDesc"
            values={{ company: <b>{declineModal.request?.companyName}</b> }}
          />
        </p>
        <div className={css.modalActions}>
          <PrimaryButton
            onClick={() => declineRequest(declineModal.request)}
            disabled={busyFor !== null}
          >
            <FormattedMessage id="General.decline" />
          </PrimaryButton>
          <SecondaryButton
            onClick={() => setDeclineModal({ open: false, request: null })}
            disabled={busyFor !== null}
          >
            <FormattedMessage id="General.cancel" />
          </SecondaryButton>
        </div>
      </Modal>

      <div className={css.openOrderForm}>
        {isRequesting || formattedPrice ? (
          <div className={css.priceContainerInCTA}>
            {isRequesting ? (
              <p className={css.price}>
                <FormattedMessage id="General.requesting" />
              </p>
            ) : (
              <>
                {formattedPrice ? (
                  <div className={css.priceValue} title={priceTitle}>
                    {is2ndLifeMarketplace &&
                      formatMoney(
                        intl,
                        subUnitsToMoney(
                          publicData?.residualValue,
                          publicData?.locale === 'no' ? 'NOK' : 'SEK'
                        )
                      )}
                    {!is2ndLifeMarketplace && formattedPrice}
                  </div>
                ) : null}

                <div className={css.perUnitInCTA}>
                  <FormattedMessage id="OrderPanel.perUnit" values={{ unitType }} />
                </div>
              </>
            )}
          </div>
        ) : null}

        {isClosed ? (
          <div className={css.closedListingButton}>
            <FormattedMessage id="OrderPanel.closedListingButtonText" />
          </div>
        ) : (
          <PrimaryButton
            onClick={() => {
              if (isRequesting) {
                openOfferListingModal();
              } else {
                openOrderModal(isOwnListing, isClosed, history, location);
              }
            }}
            disabled={isOutOfStock}
          >
            {isRequesting ? (
              <FormattedMessage id="OrderPanel.ctaButtonMessagePlaceAnOffer" />
            ) : isBooking ? (
              <FormattedMessage id="OrderPanel.ctaButtonMessageBooking" />
            ) : isOutOfStock ? (
              <FormattedMessage id="OrderPanel.ctaButtonMessageNoStock" />
            ) : is2ndLifeMarketplace ? (
              <>
                <FormattedMessage id="General.ownerDetails" />/
                <FormattedMessage id="General.actions" />
              </>
            ) : (
              <FormattedMessage id="OrderPanel.ctaButtonMessagePurchase" />
            )}
          </PrimaryButton>
        )}
      </div>
    </div>
  );
};

OrderPanel.defaultProps = {
  rootClassName: null,
  className: null,
  titleClassName: null,
  isOwnListing: false,
  titleDesktop: null,
  subTitle: null,
  monthlyTimeSlots: null,
  lineItems: null,
  fetchLineItemsError: null,
};

OrderPanel.propTypes = {
  rootClassName: string,
  className: string,
  titleClassName: string,
  listing: oneOfType([propTypes.listing, propTypes.ownListing]),
  isOwnListing: bool,
  onSubmit: func.isRequired,
  title: oneOfType([node, string]).isRequired,
  titleDesktop: node,
  subTitle: oneOfType([node, string]),
  onManageDisableScrolling: func.isRequired,

  onFetchTimeSlots: func.isRequired,
  monthlyTimeSlots: object,
  onFetchTransactionLineItems: func.isRequired,
  onContactUser: func,
  lineItems: array,
  fetchLineItemsInProgress: bool.isRequired,
  fetchLineItemsError: propTypes.error,
  marketplaceCurrency: string.isRequired,
  dayCountAvailableForBooking: number.isRequired,
  marketplaceName: string.isRequired,

  // from withRouter
  history: shape({
    push: func.isRequired,
  }).isRequired,
  location: shape({
    search: string,
  }).isRequired,

  // from injectIntl
  intl: intlShape.isRequired,
};

export default compose(withRouter, injectIntl)(OrderPanel);
